/*
 |--------------------------------------------------------------------------
 | Popover
 |
 | This module adds functionality to the Sitecore and Angular version of
 | the popover component
 |--------------------------------------------------------------------------
 */

// Constants
const APopoverSelector = '.a-popover';
const APopoverOpenAttribute = 'open';
const APopoverTargetAttribute = 'data-popover-target';
const APopoverTargetOffsetYAttribute = `${APopoverTargetAttribute}-offset-y`;
const APopoverTargetOffsetXAttribute = `${APopoverTargetAttribute}-offset-x`;
const APopoverTargetAnchorAttribute = `${APopoverTargetAttribute}-anchor`;
const APopoverTargetCssAnchorAttribute = `${APopoverTargetAttribute}-css-anchor`;
const APopoverAnchorBottomLeft = 'bottom-left';
const APopoverAnchorBottomRight = 'bottom-right';
const APopoverOpeningClass = 'a-popover--opening';
const APopoverClosingClass = 'a-popover--closing';
const APopoverCssAnchorClass = 'a-popover--css-anchor';
const APopoverCssAnchorRightClass = 'a-popover--css-anchor-right';

// Class
class APopOver {
  constructor(element) {
    this.element = element;
  }

  init = () => {
    const id = this.element.id;
    if (!id) {
      return;
    }

    this.trigger = document.querySelector(
      `[${APopoverTargetAttribute}="${id}"]`
    );
    if (!this.trigger) {
      return;
    }

    this.trigger.setAttribute('aria-controls', id);
    this.trigger.setAttribute('aria-haspopup', 'dialog');
    this.syncA11yState();

    this.useCssAnchorPositioning =
      this.trigger.hasAttribute(APopoverTargetCssAnchorAttribute) &&
      this.supportsCssAnchorPositioning();

    if (this.useCssAnchorPositioning) {
      this.initCssAnchorPositioning(this.trigger);
    }

    /**
     * Positioning situations
     */
    if (!this.useCssAnchorPositioning) {
      // Initial
      this.position(this.trigger);

      // On window resize
      new ResizeObserver(() => {
        this.position(this.trigger);
      }).observe(document.body);

      // On scroll
      window.addEventListener(
        'scroll',
        (event) => {
          if (this.isVisible()) {
            this.position(this.trigger);
          }
        },
        true
      );
    }

    // Open/close popover on trigger click
    this.trigger.addEventListener('click', () => {
      this.toggle();
      if (this.isVisible() && !this.useCssAnchorPositioning) {
        this.position(this.trigger);
      }
    });

    // Close on clicks that are not inside popover or trigger
    window.addEventListener('click', (event) => {
      if (this.isOutside(event.target) && this.isVisible()) {
        this.hide();
      }
    });

    /*
    Fix specifically for cases where popover is inside a drawer.
    Because drawers are closed on page load we need to trigger positioning when a drawer is opened.
    */
    document.querySelectorAll('dialog.a-drawer').forEach((drawer) => {
      drawer.addEventListener('drawerOpen', () => {
        if (!this.useCssAnchorPositioning) {
          this.position(this.trigger);
        }
      });
    });
  };

  toggle = () => {
    if (this.isVisible()) {
      this.hide();
    } else {
      this.show();
    }
  };
  isVisible = () => this.element.hasAttribute(APopoverOpenAttribute);
  syncA11yState = () => {
    const visible = this.isVisible();

    this.trigger.setAttribute('aria-expanded', String(visible));
    this.element.setAttribute('aria-hidden', String(!visible));

    if (visible) {
      this.element.removeAttribute('inert');
    } else {
      this.element.setAttribute('inert', '');
    }
  };
  isOutside = (element) =>
    !this.element.contains(element) && !this.trigger.contains(element);
  show = () => {
    this.element.setAttribute(APopoverOpenAttribute, '');
    this.syncA11yState();

    this.element.classList.add(APopoverOpeningClass);
    this._onAnimationEnd(this.element, () => {
      this.element.classList.remove(APopoverOpeningClass);
    });

    this.element.dispatchEvent(new Event('popoverOpened'));
  };
  hide = () => {
    this.element.classList.add(APopoverClosingClass);
    this._onAnimationEnd(this.element, () => {
      this.element.classList.remove(APopoverClosingClass);
      this.element.removeAttribute(APopoverOpenAttribute);
      this.syncA11yState();
      this.element.dispatchEvent(new Event('popoverClosed'));
    });
  };
  _onAnimationEnd = (element, callback) => {
    const styles = getComputedStyle(element);
    const hasAnimation =
      styles.animationName !== 'none' &&
      parseFloat(styles.animationDuration) > 0;

    if (!hasAnimation) {
      callback();
      return;
    }

    element.addEventListener('animationend', () => callback(), { once: true });
  };

  supportsCssAnchorPositioning = () => {
    if (typeof CSS === 'undefined' || typeof CSS.supports !== 'function') {
      return false;
    }

    return (
      CSS.supports('anchor-name: --a-popover-anchor') &&
      CSS.supports('position-anchor: --a-popover-anchor') &&
      CSS.supports('inset-inline-start: anchor(left)') &&
      CSS.supports('inset-block-start: anchor(bottom)')
    );
  };

  initCssAnchorPositioning = (trigger) => {
    const triggerOffsetY = Number(
      trigger.getAttribute(APopoverTargetOffsetYAttribute) || '12'
    );
    const triggerOffsetX = Number(
      trigger.getAttribute(APopoverTargetOffsetXAttribute) || '0'
    );
    const preferredAnchor =
      trigger.getAttribute(APopoverTargetAnchorAttribute) ||
      APopoverAnchorBottomLeft;
    const anchorName = `--a-popover-anchor-${this.element.id}`;

    this.trigger.style.setProperty('anchor-name', anchorName);
    this.element.style.setProperty('position-anchor', anchorName);
    this.element.style.setProperty(
      '--_a-popover-anchor-offset-x',
      `${triggerOffsetX}px`
    );
    this.element.style.setProperty(
      '--_a-popover-anchor-offset-y',
      `${triggerOffsetY}px`
    );
    this.element.classList.add(APopoverCssAnchorClass);
    this.element.classList.toggle(
      APopoverCssAnchorRightClass,
      preferredAnchor === APopoverAnchorBottomRight
    );
  };

  position = (trigger) => {
    if (this.useCssAnchorPositioning) {
      return;
    }

    // Hardcoded offset for Y axis against trigger to give gap between a button and popover
    const triggerOffsetY = Number(
      trigger.getAttribute(APopoverTargetOffsetYAttribute) || '12'
    );
    const triggerOffsetX = Number(
      trigger.getAttribute(APopoverTargetOffsetXAttribute) || '0'
    );
    const preferredAnchor =
      trigger.getAttribute(APopoverTargetAnchorAttribute) ||
      APopoverAnchorBottomLeft;
    const triggerRect = trigger.getBoundingClientRect();

    // Get dimensions by quickly showing and hiding element, if hidden
    const hidden = !this.isVisible();
    if (hidden) {
      this.element.setAttribute(APopoverOpenAttribute, '');
    }
    const elementDimensions = {
      width: this.element.offsetWidth,
      height: this.element.offsetHeight,
    };
    if (hidden) {
      this.element.removeAttribute(APopoverOpenAttribute);
    }

    const popoverPos = {
      x:
        preferredAnchor === APopoverAnchorBottomRight
          ? triggerRect.right - elementDimensions.width + triggerOffsetX
          : triggerRect.left + triggerOffsetX,
      y: triggerRect.bottom + triggerOffsetY,
    };

    if (triggerRect.right + elementDimensions.width > window.innerWidth) {
      popoverPos.x = triggerRect.right - elementDimensions.width;
    }
    if (
      triggerRect.bottom + elementDimensions.height > window.innerHeight &&
      triggerRect.top - elementDimensions.height > 0 // only if element truly fits
    ) {
      popoverPos.y =
        triggerRect.top - elementDimensions.height - triggerOffsetY;
    }

    this.element.style.setProperty(
      '--_a-popout-position-x',
      `${popoverPos.x}px`
    );
    this.element.style.setProperty(
      '--_a-popout-position-y',
      `${popoverPos.y}px`
    );
  };
}

// Global show popover function
window.showPopover = (popoverId) => {
  popover = document.getElementById(popoverId);
  if (popover === null) {
    throw new Error('No popover found with id="' + popoverId + '".');
  }

  popover.popoverClass.show();
};

// Global close popover function
window.closePopover = (popoverId) => {
  popover = document.getElementById(popoverId);
  if (popover === null) {
    throw new Error('No popover found with id="' + popoverId + '".');
  }

  popover.popoverClass.hide();
};

// Position all popovers
window.positionPopovers = () => {
  document.querySelectorAll(`${APopoverSelector}`).forEach((popover) => {
    if (popover.popoverClass.trigger) {
      popover.popoverClass.position(popover.popoverClass.trigger);
    }
  });
};

// Initiate module
function APopOverCreateInstance(element) {
  if (!element.popoverClass) {
    element.popoverClass = new APopOver(element);
    element.popoverClass.init();
  }
}

// Create new Popover instances on DOM mutation
document
  .querySelectorAll(`${APopoverSelector}`)
  .forEach(APopOverCreateInstance);

const APopOverObserver = new MutationObserver(function (mutations) {
  mutations.forEach((mutation) => {
    mutation.addedNodes.forEach((element) => {
      document
        .querySelectorAll(`${APopoverSelector}`)
        .forEach(APopOverCreateInstance);

      // Close all popovers if a modal is opened
      const modals = document.querySelectorAll(
        '.modal:not([hidden]), .a-modal:not([hidden])'
      );
      if (modals.length > 0) {
        document.querySelectorAll(`${APopoverSelector}`).forEach((popover) => {
          popover.popoverClass.hide();
        });
      }
    });
  });
});

APopOverObserver.observe(document, {
  attributes: false,
  childList: true,
  characterData: false,
  subtree: true,
});
